import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Platform,
  Modal,
  ScrollView,
} from 'react-native';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, borderRadius, typography } from '~constants/design';
import { PaymentIntentResponse } from '~types/api/event';
import { STRIPE_PUBLISH_KEY } from '@env';

interface StripePaymentWebStubProps {
  clientSecret: string;
  onPaymentSuccess: (data: any) => void;
  onPaymentError: (error: string) => void;
  onClose?: () => void;
  visible: boolean;
}

/**
 * Real Stripe payment integration for web
 * Uses Stripe.js and Stripe Elements for secure payment processing
 */
const StripePaymentWebStub: React.FC<StripePaymentWebStubProps> = ({
  clientSecret,
  onPaymentSuccess,
  onPaymentError,
  onClose,
  visible,
}) => {
  const { colors } = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [stripe, setStripe] = useState<any>(null);
  const [elements, setElements] = useState<any>(null);
  const [cardNumberElement, setCardNumberElement] = useState<any>(null);
  const [expiryElement, setExpiryElement] = useState<any>(null);
  const [cvcElement, setCvcElement] = useState<any>(null);

  // Initialize Stripe on component mount
  useEffect(() => {
    if (Platform.OS !== 'web' || !visible) return;

    const initializeStripe = async () => {
      try {
        // Get Stripe from global window object (loaded via CDN in index.web.js)
        const globalWindow = (global as any);
        if (globalWindow.Stripe) {
          // Use window.__STRIPE_PUBLIC_KEY__ set in index.web.js or fallback to window.STRIPE_PUBLIC_KEY
          const stripeKey = (globalWindow.__STRIPE_PUBLIC_KEY__ || globalWindow.STRIPE_PUBLIC_KEY || STRIPE_PUBLISH_KEY);
          
          if (!stripeKey) {
            console.warn('⚠️ Stripe public key not configured. Payment will not work.');
            console.warn('⚠️ Please set REACT_APP_STRIPE_PUBLIC_KEY in .env file');
            setErrorMessage('Payment form not available - please contact support');
            return;
          }
          
          const stripeInstance = globalWindow.Stripe(stripeKey);
          setStripe(stripeInstance);

          // Create elements instance
          const elementsInstance = stripeInstance.elements();
          setElements(elementsInstance);

          // Create individual card elements
          const cardNumber = elementsInstance.create('cardNumber');
          const expiry = elementsInstance.create('cardExpiry');
          const cvc = elementsInstance.create('cardCvc');
          
          setCardNumberElement(cardNumber);
          setExpiryElement(expiry);
          setCvcElement(cvc);

          // Mount card elements to DOM
          if (typeof document !== 'undefined') {
            const cardNumberDiv = document.getElementById('card-number-element');
            const expiryDiv = document.getElementById('card-expiry-element');
            const cvcDiv = document.getElementById('card-cvc-element');
            
            if (cardNumberDiv) cardNumber.mount(cardNumberDiv);
            if (expiryDiv) expiry.mount(expiryDiv);
            if (cvcDiv) cvc.mount(cvcDiv);
          }
        }
      } catch (error) {
        console.error('Failed to initialize Stripe:', error);
        setErrorMessage('Failed to load payment form');
      }
    };

    initializeStripe();

    return () => {
      // Cleanup
      if (cardNumberElement) {
        cardNumberElement.unmount();
      }
      if (expiryElement) {
        expiryElement.unmount();
      }
      if (cvcElement) {
        cvcElement.unmount();
      }
    };
  }, [visible]);

  const handlePaymentSubmit = async () => {
    if (!stripe || !cardNumberElement) {
      setErrorMessage('Payment form not ready');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      // Create payment method from individual card elements
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          // Add billing details if needed
        },
      });

      if (error) {
        // Show validation error to customer
        setErrorMessage(error.message || 'Card validation failed');
        onPaymentError(error.message || 'Card validation failed');
        setIsProcessing(false);
        return;
      }

      // Confirm the payment intent with the payment method
      const confirmResponse = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id,
      });

      const { error: confirmError, paymentIntent } = confirmResponse;

      if (confirmError) {
        // Show error to customer
        setErrorMessage(confirmError.message || 'Payment failed');
        onPaymentError(confirmError.message || 'Payment failed');
      } else if (paymentIntent) {
        if (paymentIntent.status === 'succeeded') {
          // Payment successful - show success message
          setSuccessMessage('✅ Payment successful!');
          setErrorMessage('');
          
          // Call callback with success data
          onPaymentSuccess({
            paymentIntentId: paymentIntent.id,
            status: paymentIntent.status,
            amount: paymentIntent.amount,
          });

          // Clear form fields
          if (cardNumberElement) {
            cardNumberElement.clear();
          }
          if (expiryElement) {
            expiryElement.clear();
          }
          if (cvcElement) {
            cvcElement.clear();
          }
          
          // Keep modal visible to show success message, let parent close it
        } else if (paymentIntent.status === 'requires_action') {
          // Handle 3D Secure or other authentication
          setErrorMessage('Payment requires additional authentication');
          onPaymentError('Payment requires additional authentication');
        } else {
          setErrorMessage(`Payment ${paymentIntent.status}`);
          onPaymentError(`Payment ${paymentIntent.status}`);
        }
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Payment processing failed';
      setErrorMessage(errorMsg);
      onPaymentError(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const styles = StyleSheet.create({
    modalContainer: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    },
    contentContainer: {
      backgroundColor: colors.white,
      borderTopLeftRadius: borderRadius.xl,
      borderTopRightRadius: borderRadius.xl,
      padding: spacing.lg,
      maxHeight: '90%',
    },
    header: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      marginBottom: spacing.lg,
      textAlign: 'center',
    },
    formContainer: {
      marginBottom: spacing.lg,
    },
    label: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    errorContainer: {
      backgroundColor: colors.error + '15',
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderLeftWidth: 4,
      borderLeftColor: colors.error,
    },
    errorText: {
      color: colors.error,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    payButton: {
      backgroundColor: colors.primary,
      borderRadius: borderRadius.lg,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: spacing.md,
      minHeight: 48,
    },
    payButtonDisabled: {
      opacity: 0.6,
    },
    payButtonText: {
      color: colors.white,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
    },
    cancelButton: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: borderRadius.lg,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cancelButtonText: {
      color: colors.textPrimary,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
    },
    infoBox: {
      backgroundColor: colors.primary + '10',
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.lg,
    },
    infoText: {
      fontSize: typography.fontSize.sm,
      color: colors.primary,
      fontWeight: typography.fontWeight.medium,
    },
  });

  return (
    <Modal
      visible={visible && Platform.OS === 'web'}
      transparent={true}
      animationType="slide"
      onRequestClose={() => {
        // Only allow closing if not processing payment
        if (!isProcessing) {
          onClose?.();
        }
      }}
    >
      <View style={styles.modalContainer}>
        <ScrollView style={styles.contentContainer}>
          <Text style={styles.header}>Payment Details</Text>

          <View style={styles.formContainer}>
            <Text style={styles.label}>Card Number</Text>
            <div id="card-number-element" style={{
              borderWidth: 1,
              borderColor: (colors.border as string),
              borderRadius: borderRadius.md,
              padding: spacing.md,
              marginBottom: spacing.md,
              backgroundColor: (colors.surface as string),
              minHeight: 50,
              fontSize: '16px',
            }} />

            <View style={{ flexDirection: 'row', gap: spacing.md }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>Expiry Date</Text>
                <div id="card-expiry-element" style={{
                  borderWidth: 1,
                  borderColor: (colors.border as string),
                  borderRadius: borderRadius.md,
                  padding: spacing.md,
                  backgroundColor: (colors.surface as string),
                  minHeight: 50,
                  fontSize: '16px',
                }} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.label}>CVC</Text>
                <div id="card-cvc-element" style={{
                  borderWidth: 1,
                  borderColor: (colors.border as string),
                  borderRadius: borderRadius.md,
                  padding: spacing.md,
                  marginBottom: spacing.md,
                  backgroundColor: (colors.surface as string),
                  minHeight: 50,
                  fontSize: '16px',
                }} />
              </View>
            </View>
          </View>

          {/* Error Message */}
          {errorMessage ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{errorMessage}</Text>
            </View>
          ) : null}

          {/* Success Message */}
          {successMessage ? (
            <View style={[styles.errorContainer, { borderLeftColor: colors.primary, backgroundColor: colors.primary + '15' }]}>
              <Text style={[styles.errorText, { color: colors.primary }]}>{successMessage}</Text>
            </View>
          ) : null}

          {/* Pay Button */}
          <TouchableOpacity
            style={[styles.payButton, (isProcessing || !!successMessage) ? styles.payButtonDisabled : null]}
            onPress={handlePaymentSubmit}
            disabled={isProcessing || !!successMessage}
          >
            {isProcessing ? (
              <ActivityIndicator color={colors.white} size="small" />
            ) : (
              <Text style={styles.payButtonText}>Complete Payment</Text>
            )}
          </TouchableOpacity>

          {/* Cancel Button */}
          <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={isProcessing}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    </Modal>
  );
};

export default StripePaymentWebStub;
